// =============================================================================
// api/overview.ts
// =============================================================================

import type { VTour } from "@/types";
import { PLAN_CONFIGS } from "@/types/subscription.types";
import { supabase } from "@cl/supabase";
import { handleSupabaseError } from "./_error";
import { fetchSessionsByTour } from "./analytics";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TourCounts {
  total: number;
  published: number;
  draft: number;
  inactive: number;
}

export interface PlanUsage {
  used: number;
  limit: number | null;
  planType: string;
}

export interface StorageUsage {
  /** Bytes currently used across the account's tour media. */
  usedBytes: number;
  /** Bytes allowed by the plan. -1 = unlimited. */
  limitBytes: number;
}

export interface ClientOverview {
  tourCounts: TourCounts;
  sessions30d: number;
  topTour: { title: string; sessions: number } | null;
  planUsage: PlanUsage | null;
  storageUsage: StorageUsage | null;
  recentTours: VTour[];
}

/** Per-product plan → user-count map (e.g. { free: 12, plus: 3 }). */
export type PlanCounts = Record<string, number>;

/** Plan distribution across the platform, per product (admin only). */
export interface PlanDistribution {
  plan360: PlanCounts;
}

export interface AdminOverview {
  /** Platform user total (used by the admin billing conversion %). */
  totalUsers: number;
  /** Month-to-date signups across the platform. */
  newUsersThisMonth: number;
  newToursThisMonth: number;
  newPaidSubsThisMonth: number;
  planDistribution: PlanDistribution;
}

/** Latest items across the whole platform (admin overview). Admins hold no
 *  per-product plan — each tour/venue carries its own resource-first `plan`. */
export interface AdminRecent {
  tours: {
    id: string;
    title: string;
    created_at: string;
    owner_name: string | null;
    plan: string;
  }[];
  tours_total: number;
  venues: {
    id: string;
    name: string;
    created_at: string;
    owner_name: string | null;
    plan: string;
  }[];
  venues_total: number;
  users: { id: string; name: string | null; created_at: string }[];
  users_total: number;
}

export async function fetchAdminRecent(): Promise<AdminRecent> {
  const { data, error } = await supabase.rpc("admin_recent_platform", {
    p_limit: 5,
  });
  if (error) handleSupabaseError(error, "fetchAdminRecent");
  return (
    (data as AdminRecent) ?? {
      tours: [],
      tours_total: 0,
      venues: [],
      venues_total: 0,
      users: [],
      users_total: 0,
    }
  );
}

/** Snapshot de la suscripción. */
export interface ProductPlan {
  planType: string;
  currentPeriodEnd: string | null;
}

/** Cross-cutting account summary — not product-scoped (leads/bookings/billing). */
export interface AccountSummary {
  leadsTotal: number;
  leadsNew: number;
  bookingsPending: number;
  bookingsUpcoming: number;
  /** Billing is per product. Free = absence of a row, so each defaults to its free tier. */
  plan360: ProductPlan;
}

// ── Storage usage ─────────────────────────────────────────────────────────────

export async function fetchStorageUsage(): Promise<StorageUsage> {
  const { data, error } = await supabase.rpc("get_storage_usage");
  if (error) handleSupabaseError(error, "fetchStorageUsage");
  const row = (data ?? [])[0] as { used_bytes: number; limit_bytes: number } | undefined;
  return {
    usedBytes: Number(row?.used_bytes ?? 0),
    limitBytes: Number(row?.limit_bytes ?? 0),
  };
}

/** Storage usage + cap for a specific tour (owner's usage, tour's effective
 *  plan). Unlike fetchStorageUsage(), it never returns admin-unlimited — the
 *  tour editor bar must show the tour's real plan cap regardless of viewer. */
export async function fetchTourStorageUsage(tourId: string): Promise<StorageUsage> {
  const { data, error } = await supabase.rpc("get_tour_storage_usage", {
    p_tour_id: tourId,
  });
  if (error) handleSupabaseError(error, "fetchTourStorageUsage");
  const row = (data ?? [])[0] as { used_bytes: number; limit_bytes: number } | undefined;
  return {
    usedBytes: Number(row?.used_bytes ?? 0),
    limitBytes: Number(row?.limit_bytes ?? 0),
  };
}

// ── Client overview ───────────────────────────────────────────────────────────

export async function fetchClientOverview(userId: string): Promise<ClientOverview> {
  const since30d = new Date();
  since30d.setDate(since30d.getDate() - 30);

  // Tours first — needed to build the tour_id filter for sessions
  const toursRes = await supabase
    .from("v_tours")
    .select("*")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });

  if (toursRes.error) handleSupabaseError(toursRes.error, "fetchClientOverview:tours");

  const tours = (toursRes.data ?? []) as VTour[];
  const userTourIds = tours.map((t) => t.id);

  // Tour counts by state
  const tourCounts: TourCounts = {
    total: tours.length,
    published: tours.filter((t) => t.state === "published").length,
    draft: tours.filter((t) => t.state === "draft").length,
    inactive: tours.filter((t) => t.state === "inactive").length,
  };

  // Sessions, subscription and storage usage in parallel. Sessions-by-tour is
  // the shared read (fetchSessionsByTour) so overview + analytics never drift.
  const [sessionsByTourObj, subRes, storageUsage] = await Promise.all([
    fetchSessionsByTour(userTourIds, 30),

    supabase
      .from("subscriptions")
      .select("plan_type, status")
      .eq("user_id", userId)
      .maybeSingle(),

    fetchStorageUsage(),
  ]);

  const sessionsByTour = new Map<string, number>(Object.entries(sessionsByTourObj));
  const sessions30d = [...sessionsByTour.values()].reduce((a, b) => a + b, 0);

  // Top tour by session count
  let topTour: ClientOverview["topTour"] = null;
  if (sessionsByTour.size > 0) {
    const [topId, topCount] = [...sessionsByTour.entries()].sort((a, b) => b[1] - a[1])[0];
    const topTourData = tours.find((t) => t.id === topId);
    if (topTourData) {
      topTour = { title: topTourData.title, sessions: topCount };
    }
  }

  // Plan usage
  let planUsage: PlanUsage | null = null;
  const sub = subRes.data;
  if (sub) {
    const config = PLAN_CONFIGS.find((p) => p.type === sub.plan_type);
    planUsage = {
      used: tourCounts.total,
      limit: config?.tourLimit ?? null,
      planType: sub.plan_type,
    };
  }

  // Recent 4 tours
  const recentTours = tours.slice(0, 4);

  return { tourCounts, sessions30d, topTour, planUsage, storageUsage, recentTours };
}

// ── Account summary (cross-cutting) ─────────────────────────────────────────────

export async function fetchAccountSummary(userId: string): Promise<AccountSummary> {
  const nowIso = new Date().toISOString();

  // RLS scopes leads/bookings/subscriptions to the owner, so no extra
  // user filter is needed for the lead tables (mirrors fetchAllUserLeads). Each
  // count degrades to 0 independently.
  const [
    tourLeadsRes,
    tourLeadsNewRes,
    bookingsPendingRes,
    bookingsUpcomingRes,
    subRes,
  ] = await Promise.all([
    supabase.from("tour_leads").select("id", { count: "exact", head: true }),
    supabase.from("tour_leads").select("id", { count: "exact", head: true }).eq("status", "new"),
    supabase
      .from("bookings")
      .select("id", { count: "exact", head: true })
      .eq("owner_id", userId)
      .eq("status", "pending"),
    supabase
      .from("bookings")
      .select("id", { count: "exact", head: true })
      .eq("owner_id", userId)
      .in("status", ["pending", "confirmed"])
      .gte("start_at", nowIso),
    supabase
      .from("subscriptions")
      .select("plan_type, current_period_end")
      .eq("user_id", userId)
      .maybeSingle(),
  ]);

  type SubRow = { plan_type: string; current_period_end: string | null } | null;
  const toPlan = (row: SubRow, free: string): ProductPlan => ({
    planType: row?.plan_type ?? free,
    currentPeriodEnd: row?.current_period_end ?? null,
  });

  return {
    leadsTotal: tourLeadsRes.count ?? 0,
    leadsNew: tourLeadsNewRes.count ?? 0,
    bookingsPending: bookingsPendingRes.count ?? 0,
    bookingsUpcoming: bookingsUpcomingRes.count ?? 0,
    plan360: toPlan(subRes.data as SubRow, "free"),
  };
}

// ── Admin overview ────────────────────────────────────────────────────────────

export async function fetchAdminOverview(): Promise<AdminOverview> {
  const [monthRes, distRes, usersRes] = await Promise.all([
    // Month-to-date signups (users/tours/paid subs) — admin-gated RPC
    // so it bypasses per-owner RLS.
    supabase.rpc("admin_month_stats"),
    // Per-product plan counts. Same RLS caveat → SECURITY DEFINER RPC.
    supabase.rpc("admin_plan_distribution"),
    // Platform user total (billing conversion %).
    supabase.from("profiles").select("id", { count: "exact", head: true }),
  ]);

  const planDistribution: PlanDistribution = { plan360: {} };
  const bucket: Record<string, keyof PlanDistribution> = { "360": "plan360" };
  for (const row of (distRes.data ?? []) as {
    product: string;
    plan_type: string;
    cnt: number;
  }[]) {
    const key = bucket[row.product];
    if (key) planDistribution[key][row.plan_type] = Number(row.cnt);
  }

  const m = (monthRes.data ?? {}) as {
    new_users?: number;
    new_tours?: number;
    new_paid_subs?: number;
  };

  return {
    totalUsers: usersRes.count ?? 0,
    newUsersThisMonth: Number(m.new_users ?? 0),
    newToursThisMonth: Number(m.new_tours ?? 0),
    newPaidSubsThisMonth: Number(m.new_paid_subs ?? 0),
    planDistribution,
  };
}
